import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FlaskConical, ArrowLeft, Brain } from 'lucide-react';
import { api } from '@/api/api';
import { toast } from 'sonner';
import { ScoreInfoDialog } from '@/components/ScoreInfoDialogs';
import { useNavigate } from 'react-router-dom';
import VisibilityScoreBreakdown from '@/components/VisibilityScoreBreakdown';
import { ThemeToggle } from '@/components/ThemeToggle';
import PageSEO from '@/components/PageSEO';
import { ArticleToolbar } from '@/components/article/ArticleToolbar';

import { ImpactScoreCards } from '@/components/article/ImpactScoreCards';
import { ScoreBreakdownBar } from '@/components/article/ScoreBreakdownBar';
import { CaseEvaluationPanel } from '@/components/article/CaseEvaluationPanel';
import { EvidenceSynthesisPanel } from '@/components/article/EvidenceSynthesisPanel';
import { RelatedArticles } from '@/components/article/RelatedArticles';

const EVIDENCE_SYNTHESIS_TYPES = ['Meta-analysis', 'Systematic review', 'Scoping review', 'Narrative review', 'Critical appraisal review'];

const ArticleDetail = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [caseEvaluation, setCaseEvaluation] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);

  useEffect(() => {
    loadArticle();
    loadRelatedArticles();
    loadPdfInfo();
    trackView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  const trackView = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${API_URL}/api/articles/${articleId}/track-view`, { method: 'POST' });
    } catch (error) {
      void 0;
    }
  };

  useEffect(() => {
    if (article && (article.normalized_study_type === 'Case report' || article.normalized_study_type === 'Case series')) {
      loadCaseEvaluation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article]);

  const loadArticle = async () => {
    setLoading(true);
    try { setArticle(await api.getPublicArticleDetail(articleId)); } catch { toast.error('Failed to load article details'); }
    setLoading(false);
  };

  const loadRelatedArticles = async () => {
    try { setRelatedArticles(await api.getRelatedArticles(articleId, 5)); } catch (e) { void 0; }
  };

  const loadCaseEvaluation = async (retries = 3) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/articles/${articleId}/case-evaluation`);
      if (response.ok) {
        setCaseEvaluation(await response.json());
      } else if (response.status === 404 && retries > 0) {
        setTimeout(() => loadCaseEvaluation(retries - 1), 5000);
      }
    } catch (e) { void 0; }
  };

  const loadPdfInfo = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/articles/${articleId}/pdf-info`);
      if (response.ok) setPdfInfo(await response.json());
    } catch (e) { void 0; }
  };

  const handleDownloadPdf = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${API_URL}/api/articles/${articleId}/download-pdf`, '_blank');
    setTimeout(() => loadPdfInfo(), 1000);
  };

  const handleAnalyzeArticle = async () => {
    setAnalyzing(true);
    try {
      toast.info('AI Analysis started. This may take 30-60 seconds...');
      await api.analyzeArticle(articleId);
      toast.success('Analysis complete!');
      navigate(`/research-insight/${articleId}`);
    } catch (error) {
      toast.error('Failed to analyze article: ' + error.message);
    } finally { setAnalyzing(false); }
  };

  const getEvidenceLevelColor = (level) => {
    switch (level) {
      case 'Level I': return 'bg-green-100 text-green-800';
      case 'Level II': return 'bg-blue-100 text-blue-800';
      case 'Level III': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isCaseReport = article?.normalized_study_type === 'Case report' || article?.normalized_study_type === 'Case series';
  const isEvidenceSynthesis = EVIDENCE_SYNTHESIS_TYPES.includes(article?.normalized_study_type);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><p className="text-slate-600">Loading article details...</p></div>;
  if (!article) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><p className="text-slate-600">Article not found</p></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {article && (
        <PageSEO
          title={`${article.title} | Evidentica`}
          description={article.abstract?.substring(0, 155) || `Research article: ${article.title}`}
          keywords={`${article.normalized_study_type || ''}, ${article.journal || ''}, medical research, evidence-based medicine`}
          canonical={`https://evidentica.net/article/${articleId}`}
          article={true}
        />
      )}
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center space-x-2">
              <FlaskConical className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />
              <span className="text-lg sm:text-xl font-bold text-slate-900">Evidentica</span>
            </Link>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <span className="hidden sm:inline"><ScoreInfoDialog /></span>
              <ThemeToggle />
              <Link to="/explorer">
                <Button variant="ghost" size="sm" data-testid="back-to-explorer-btn">
                  <ArrowLeft className="mr-1 sm:mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Back to Explorer</span>
                  <span className="sm:hidden">Explorer</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Article Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-testid="article-title">{article.title}</h1>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm sm:text-base text-slate-600">
            {article.journal && <span className="font-medium" data-testid="article-journal">{article.journal}</span>}
            {article.publication_year && <span data-testid="article-year">({article.publication_year})</span>}
            {article.doi && <span className="text-xs sm:text-sm truncate max-w-[200px] sm:max-w-none" data-testid="article-doi">DOI: {article.doi}</span>}
          </div>
          {article.authors && article.authors.length > 0 && (
            <div className="mt-3 text-sm text-slate-600" data-testid="article-authors">
              {article.authors.slice(0, 5).join(', ')}
              {article.authors.length > 5 && ` et al. (${article.authors.length} authors)`}
            </div>
          )}
        </div>

        {/* Badges and Buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <div className="flex items-center flex-wrap gap-2">
            {article.normalized_study_type && <Badge variant="outline" className="text-sm sm:text-base py-1 px-2 sm:px-3" data-testid="study-type-badge">{article.normalized_study_type}</Badge>}
            {article.evidence_level && <Badge className={`${getEvidenceLevelColor(article.evidence_level)} text-sm sm:text-base py-1 px-2 sm:px-3`} data-testid="evidence-level-badge">{article.evidence_level}</Badge>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {pdfInfo && pdfInfo.has_pdf && (
              <Button onClick={handleDownloadPdf} variant="outline" size="sm" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                <svg className="mr-1 sm:mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                PDF ({pdfInfo.download_count || 0})
              </Button>
            )}
            {!isCaseReport && !isEvidenceSynthesis && (
              <Button onClick={handleAnalyzeArticle} disabled={analyzing} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="analyze-article-btn">
                <Brain className={`mr-1 sm:mr-2 h-4 w-4 ${analyzing ? 'animate-pulse' : ''}`} />
                {analyzing ? 'Analyzing...' : 'AI Analysis'}
              </Button>
            )}
          </div>
        </div>

        <ImpactScoreCards article={article} />
        <ScoreBreakdownBar article={article} />

        {isCaseReport && <CaseEvaluationPanel caseEvaluation={caseEvaluation} />}
        {isEvidenceSynthesis && <EvidenceSynthesisPanel articleId={articleId} />}

        {/* Key Takeaway - Case Reports */}
        {isCaseReport && caseEvaluation?.evaluation_explanation && (
          <Card className="mb-8 bg-green-50 border-green-200" data-testid="key-takeaway-card">
            <CardHeader><CardTitle className="text-green-900">Key Takeaway</CardTitle></CardHeader>
            <CardContent><p className="text-base text-green-900 font-medium">{caseEvaluation.evaluation_explanation}</p></CardContent>
          </Card>
        )}

        {/* How to Cite - Case Reports */}
        {isCaseReport && article.doi && (
          <Card className="mb-8 border-blue-200" data-testid="how-to-cite-card">
            <CardHeader><CardTitle className="text-blue-900">How to Cite This Article</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1">APA Style</p>
                  <p className="text-sm bg-slate-50 p-3 rounded border border-slate-200 font-mono">
                    {article.authors?.length > 0 ? `${article.authors[0]}${article.authors.length > 1 ? ' et al.' : ''}` : 'Author(s)'} ({article.publication_year || 'n.d.'}). {article.title}. <em>{article.journal || 'Journal'}</em>. https://doi.org/{article.doi}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1">Vancouver Style</p>
                  <p className="text-sm bg-slate-50 p-3 rounded border border-slate-200 font-mono">
                    {article.authors?.length > 0 ? `${article.authors.slice(0, 3).join(', ')}${article.authors.length > 3 ? ', et al' : ''}` : 'Author(s)'}. {article.title}. {article.journal || 'Journal'}. {article.publication_year || ''}. DOI: {article.doi}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Visibility Score Breakdown */}
        {article.impact_index && (
          <div className="mb-8" data-testid="visibility-breakdown-section"><VisibilityScoreBreakdown articleId={articleId} /></div>
        )}

        {/* Study Characteristics */}
        <Card className="mb-8" data-testid="study-characteristics-card">
          <CardHeader><CardTitle>Study Characteristics</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div><p className="text-sm text-slate-600 mb-1">Study Design</p><p className="font-medium" data-testid="study-design">{article.normalized_study_type || 'Not specified'}</p></div>
              <div><p className="text-sm text-slate-600 mb-1">Evidence Level</p><p className="font-medium" data-testid="evidence-level">{article.evidence_level || 'Not specified'}</p></div>
              <div><p className="text-sm text-slate-600 mb-1">Sample Size</p><p className="font-medium" data-testid="sample-size">{article.sample_size || 'Not specified'}</p></div>
              <div><p className="text-sm text-slate-600 mb-1">Follow-up Duration</p><p className="font-medium" data-testid="follow-up-duration">{article.follow_up_duration_months ? `${article.follow_up_duration_months} months` : 'Not specified'}</p></div>
            </div>
          </CardContent>
        </Card>

        {/* Topics */}
        {article.topics?.length > 0 && (
          <Card className="mb-8" data-testid="topics-card">
            <CardHeader><CardTitle>Topics</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">{article.topics.map((topic, i) => <Badge key={i} variant="secondary" data-testid="topic-badge">{topic}</Badge>)}</div>
            </CardContent>
          </Card>
        )}

        <RelatedArticles articles={relatedArticles} />

        {/* Citation, Export & Share Toolbar */}
        <ArticleToolbar article={article} articleId={articleId} />

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            <strong>Important:</strong> The Impact Index is a methodological indicator designed to assess study design strength
            and research visibility. It does not represent clinical truth, guideline recommendations, or treatment standards.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetail;
